import React, { FC, useMemo, useState, useCallback } from "react";
import { themeOptions } from "styles/theme";

import { format } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import { Sizes } from "./sizes";
import me from "models/me";
import _ from "lodash";
import classNames from "classnames";
import CircularProgress from "material-ui/CircularProgress";
import moment from "moment";
import IconButton from "material-ui/IconButton";
import FileCopy from "material-ui/svg-icons/content/content-copy";
import { hasInventory } from "./inventory-helpers";
import { getArrivingTable } from "helpers/inventory";
import Grid from "@material-ui/core/Grid";
import SelectField from "components/form/select-field";
import { AddHintBadge } from "components/hint-badge";
import { InfoIconHover } from "components/icons/info-icon-hover";
import { Form } from "formsy-react";
import FormLabel from "@material-ui/core/FormLabel";
import { DATATEST } from "constants/integration";
import { FormControlLabel, Radio, RadioGroup, makeStyles } from "@material-ui/core";
import MenuItem from "material-ui/MenuItem";
import type { IItemRecord } from "records/item/item";
import type { IInventoryMap } from "types/inventory";

export interface IWarehouse {
    id: string;
    display_name: string;
    code: string;
}

interface IProps {
    product: IItemRecord;
    inventoryMap?: IInventoryMap;
    isFetching?: boolean;
    warehouses: IWarehouse[];
    warehouseId?: string;
    onWarehouseChange: (event, index: number, warehouseId: string) => void;
    scope: IScope;
    onClickDuplicate?: (date: moment.Moment) => void;
    classes?: any;
    enforceAvailableUntil?: boolean;
    warehousesSelectorLabel?: string;
    showRadioHint?: boolean;
    showHeader?: boolean;
}

enum UNITS_TYPE {
    CUMULATIVE = "cumulative",
    ARRIVING = "arriving",
}

export const ProductInventory: FC<IProps> = (props: IProps) => {
    const inner = useStyles();

    const UNITS_FILTERS = useMemo(
        () => [
            {
                dataTest: DATATEST.PRODUCT_INVENTORY.TOTAL_UNITS_FILTER,
                value: UNITS_TYPE.CUMULATIVE,
                label: format("Total units"),
                hint: format("All available inventory on the selected delivery date."),
                hintDataTest: DATATEST.PRODUCT_INVENTORY.UNITS_HINT,
            },
            {
                dataTest: DATATEST.PRODUCT_INVENTORY.UNITS_ARRIVING_FILTER,
                value: UNITS_TYPE.ARRIVING,
                label: format("Units arriving"),
                hint: format("All inventory arriving on the selected delivery date."),
                hintDataTest: DATATEST.PRODUCT_INVENTORY.UNITS_HINT,
            },
        ],
        [],
    );

    const { product, inventoryMap, isFetching } = props;
    const { warehouses, warehouseId } = props;
    const { enforceAvailableUntil, scope, showRadioHint, showHeader = true } = props;
    const { classes, warehousesSelectorLabel = format("Showing inventory for") } = props;
    const { onWarehouseChange, onClickDuplicate } = props;

    const [isCumulative, setIsCumulative] = useState<boolean>(true);

    const sizeGroups = useMemo(() => _.values(product?.getSizeGroups()), [product]);
    const sizes = useMemo(() => _.map(product?.__sizes.toJS(), "_id"), [product]);
    const limits = useMemo(
        () =>
            scope?.brand?._brandConnection?.hide_full_inventory || me.isBuyer()
                ? { max: scope?.brand?.getPreference("inventory_maximum_threshold") }
                : null,
        [scope?.brand],
    );
    const inventoryTable = useMemo(
        () => getArrivingTable(_.pick(inventoryMap?.[warehouseId], sizes), limits),
        [inventoryMap, sizes, warehouseId, limits],
    );

    const handleDisplayInventoryAsChange = useCallback(
        (e, value) => setIsCumulative(value === UNITS_TYPE.CUMULATIVE),
        [],
    );
    const handleDuplicateClick = useCallback((periodStart) => () => onClickDuplicate(periodStart), [onClickDuplicate]);

    const viewInventory = useMemo(() => hasInventory(product), [product]);

    const getQuantity = useCallback(
        (row: number, sku: string) =>
            _.get(inventoryTable?.[row]?.skus?.[sku]?.display, isCumulative ? "total" : "arriving"),
        [isCumulative, inventoryTable],
    );

    const getTotal = useCallback(
        (row: number) => _.get(inventoryTable?.[row], isCumulative ? "total" : "arriving"),
        [inventoryTable, isCumulative],
    );

    const renderRowLabel = useCallback(
        ({ rowIndex }) => {
            const periodStart = moment.utc(inventoryTable?.[rowIndex]?.date);

            const allowCopy =
                onClickDuplicate &&
                (!enforceAvailableUntil ||
                    !product?.__available_until ||
                    periodStart.isBefore(product?.__available_until));
            return (
                <div className={inner.arrivalLabel}>
                    {allowCopy && (
                        <IconButton
                            className="icon"
                            onClick={handleDuplicateClick(periodStart)}
                            tooltip={format("Duplicate this line")}
                            tooltipPosition="top-right"
                        >
                            <FileCopy />
                        </IconButton>
                    )}
                    {periodStart.format("MMM D, YYYY")}
                </div>
            );
        },
        [
            inventoryTable,
            onClickDuplicate,
            enforceAvailableUntil,
            product?.__available_until,
            inner?.arrivalLabel,
            handleDuplicateClick,
        ],
    );

    return viewInventory ? (
        <section className={inner.root} data-test="inventory-arriving-table">
            <header>{showHeader && <h3>{format("Inventory Arriving Table")}</h3>}</header>
            <Form className={classNames((inner.form, classes?.form))}>
                <Grid container={true}>
                    <Grid item={true} className={inner.filters} xs={12}>
                        {_.size(warehouses) > 1 && (
                            <div className={inner.selectContainer}>
                                <SelectField
                                    label={warehousesSelectorLabel}
                                    labelClassName={"warehouseLabel"}
                                    data-test={DATATEST.DELIVERY_MANAGEMENT.INVENTORY_ARRIVING.WAREHOUSE_SELECT}
                                    baseClassName={classNames(inner.select, classes?.select)}
                                    value={warehouseId}
                                    onChange={onWarehouseChange}
                                    name={"warehouse-select"}
                                >
                                    {_.map(warehouses, (wh) => (
                                        <MenuItem
                                            key={`product-inventory-warehouse-${wh.id}`}
                                            value={wh.id}
                                            primaryText={wh.display_name}
                                        />
                                    ))}
                                </SelectField>
                            </div>
                        )}
                        <div className={inner.radioGroupContainer}>
                            <FormLabel component="legend">{format("Units")}</FormLabel>
                            <RadioGroup
                                classes={{ root: inner.radioGroupRoot }}
                                value={isCumulative ? UNITS_TYPE.CUMULATIVE : UNITS_TYPE.ARRIVING}
                                onChange={handleDisplayInventoryAsChange}
                                data-test={DATATEST.PRODUCT_INVENTORY.UNITS_SOURCE}
                            >
                                {_.map(UNITS_FILTERS, (filter) => (
                                    <FormControlLabel
                                        key={filter.value}
                                        data-test={filter.dataTest}
                                        value={filter.value}
                                        control={
                                            <Radio
                                                classes={{ root: classNames(inner.radioButton, classes?.radioButton) }}
                                            />
                                        }
                                        classes={{
                                            root: classNames(inner.formControl, classes?.formControl),
                                            label: classNames(inner.formLabel, classes?.formLabel),
                                        }}
                                        label={
                                            showRadioHint ? (
                                                <AddHintBadge
                                                    hint={filter.hint}
                                                    hintIcon={<InfoIconHover data-test={filter.hintDataTest} />}
                                                >
                                                    {filter.label}
                                                </AddHintBadge>
                                            ) : (
                                                filter.label
                                            )
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    </Grid>
                    <Grid item={true} className={inner.sizesContainer} xs={12}>
                        <Sizes
                            sizeGroups={sizeGroups}
                            rows={_.size(inventoryTable)}
                            rowLabel={renderRowLabel}
                            showTotal={
                                me.hasPermission("display_inventory") &&
                                scope.brand.hasPreference("show_total_inventory")
                            }
                            getTotalInventory={getTotal}
                        >
                            {({ rowIndex, size }) => <div key={size._id}>{getQuantity(rowIndex, size._id)}</div>}
                        </Sizes>
                    </Grid>
                </Grid>
            </Form>
            {_.isEmpty(inventoryTable) && isFetching && (
                <div className={inner.loading}>
                    <div>
                        <CircularProgress color={themeOptions.palette.lightGrey} size={24} />
                    </div>
                </div>
            )}
            {_.isEmpty(inventoryTable) && !isFetching && (
                <div className={inner.noResults}>{format("There is no incoming inventory for this warehouse.")}</div>
            )}
        </section>
    ) : null;
};

const useStyles = makeStyles({
    root: {
        padding: "16px",
        backgroundColor: themeOptions.palette.white,
        "& .isMobile &": {
            borderTop: "0px",
            padding: "16px",
            marginTop: "0px",
            borderBottom: `1px solid ${themeOptions.palette.lightGrey}`,
        },
        "& .isMobile h3": {
            fontSize: "14px",
            margin: "0 0 8px",
            lineHeight: 1.43,
        },
        "& h3": {
            fontSize: "14px",
            fontWeight: "bold",
            lineHeight: 1.25,
            letterSpacing: "0.2px",
            color: themeOptions.palette.darkestGrey,
            margin: "0 0 24px",
        },
        "& .icon": {
            float: "left",
        },
        "& .icon svg": {
            width: "16px !important",
            height: "16px !important",
            marginTop: "4px",
            marginBottom: "-3px",
        },
    },
    form: {
        width: "100%",
    },
    select: {
        width: "100% !important",
    },
    filters: {
        display: "flex",
        margin: "0 0 24px",
    },
    selectContainer: {
        flexShrink: 0,
        flexGrow: 0,
        marginRight: "20px",
        width: "168px",
    },
    radioGroupContainer: {
        flexShrink: 0,
        flexGrow: 1,
        width: "168px",
    },
    radioGroupRoot: {
        marginTop: "5px",
    },
    formControl: {
        margin: 0,
        "& span": {
            padding: 0,
        },
    },
    formLabel: {
        fontFamily: "inherit",
        fontSize: 14,
        marginLeft: 10,
        color: `${themeOptions.palette.textColor}`,
    },
    radioButton: {
        color: `${themeOptions.palette.darkGrey}`,
        "&.Mui-checked": {
            color: `${themeOptions.palette.darkGrey}`,
        },
        "& svg": {
            fontSize: 24,
        },
    },
    arrivalLabel: {
        position: "relative",
        "& .icon": {
            width: "24px !important",
            height: "24px !important",
            padding: "0 !important",
            left: "0",
            position: "absolute !important",
        },
        "& .icon:hover svg": {
            color: `${themeOptions.palette.accent1Color} !important`,
        },
    },
    sizesContainer: {
        "& .sizeTable": {
            overflowX: "auto",
            width: "100%",
            paddingBottom: "8px",
        },
        "& .sizeCell": {
            letterSpacing: "0.1px",
            borderBottom: `1px solid ${themeOptions.palette.lightGrey}`,
        },
        "& .sizeGroupName": {
            padding: "0 8px",
        },
        "& .sizeHeader .sizeCell": {
            padding: "0 8px 4px",
            fontSize: "12px",
            fontWeight: "bold",
            lineHeight: 1.67,
            color: themeOptions.palette.darkestGrey,
            textTransform: "uppercase",
            alignSelf: "flex-end",
            whiteSpace: "pre",
            // textAlign: "right",
        },
        "& .sizeBody .sizeLabel": {
            backgroundColor: "#f0f0f0",
            textAlign: "right",
            fontSize: "10px",
            fontWeight: "bold",
            lineHeight: "28px",
            // color: themeOptions.palette.darkGrey,
        },
        "& .sizeBody .sizeCell": {
            color: themeOptions.palette.darkGrey,
            fontSize: "12px",
            padding: "0 8px",
            lineHeight: "28px",
        },
        // to avoid sticky element overlapping the last size in FF, Safari
        "& .sizesMain::after": !/\bChrome\b/.test(navigator.userAgent) && {
            content: " ",
            width: "50px",
        },
        "& .sizeBody .sizeRow .sizeCell:last-child": {
            flexGrow: 1,
        },
    },
    loading: {
        padding: "24px 0",
        textAlign: "center",
    },
    noResults: {
        padding: "24px 0",
        textAlign: "center",
    },
});
